// client/src/components/layout/Header.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { BellRing, LogOut, Menu } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from 'react-i18next';

const Header = ({ title, onToggleSidebar }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const notificationRef = useRef(null);
  const mobileNotificationRef = useRef(null);
  
  // Handle window resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Set language based on user role
  useEffect(() => {
    if (user?.role === 'admin') {
      // Force English for admin and bank users
      if (i18n.language !== 'en') {
        i18n.changeLanguage('en');
      }
    }
  }, [user, i18n]);
  
  // Function to get translated title based on user role
  const getTranslatedTitle = () => {
    if (title) {
      // Se viene passato un titolo specifico, usalo
      return title;
    }
    
    // Altrimenti, determina il titolo basandosi sul ruolo dell'utente
    switch (user?.role) {
      case 'company':
        return t('portal.companyPortal');
      case 'bank':
        return t('portal.bankPortal');
      case 'admin':
        return 'Admin Portal'; // Admin sempre in inglese
      default:
        return t('portal.companyPortal');
    }
  };
  
  // Function to translate notification title and message
  const translateNotification = (notification) => {
    // Se il titolo è già una chiave di traduzione, usala
    if (notification.titleKey) {
      return {
        title: t(`notifications.types.${notification.titleKey}`),
        message: t(`notifications.messages.${notification.messageKey}`, notification.data || {})
      };
    }
    
    // Altrimenti, prova a inferire dalla stringa esistente (backward compatibility)
    let titleKey = '';
    let messageKey = '';
    let data = {};
    
    // Mappa i titoli esistenti alle chiavi di traduzione
    const titleMapping = {
      'New Bid Received': 'new_bid_received',
      'Bid Cancelled': 'bid_cancelled', 
      'Bid Approved': 'bid_approved',
      'Bid Rejected': 'bid_rejected',
      'New Liquidity Request': 'new_request',
      'Request Completed': 'request_completed',
      'All Transfers Completed': 'all_transfers_completed',
      'Bid Rejected - Request Cancelled': 'bid_rejected_cancelled',
      'Account Approved': 'account_approved'
    };
    
    titleKey = titleMapping[notification.title] || 'noTitle';
    messageKey = titleMapping[notification.title] || 'noMessage';
    
    // Estrai dati dal messaggio per l'interpolazione
    if (notification.message) {
      // Esempi di regex per estrarre dati dinamici dal messaggio
      const bankNameMatch = notification.message.match(/([A-Za-z\s]+)\s+submitted a bid|([A-Za-z\s]+)\s+has cancelled/);
      const requestIdMatch = notification.message.match(/#(\w+)/);
      // FIX: Improved amount extraction to handle larger numbers with commas and dots
      const amountMatch = notification.message.match(/€([\d,.]+)/);
      const rateMatch = notification.message.match(/at\s+([\d.]+%|Euribor\s+\+\s+[\d.]+%)/);
      
      // FIX: Properly format the amount
      let formattedAmount = '';
      if (amountMatch) {
        // Remove both commas and dots, then parse as number
        const numericAmount = parseFloat(amountMatch[1].replace(/[,.]/g, ''));
        // Format using toLocaleString for proper currency display (without € since it's already in the original)
        formattedAmount = numericAmount.toLocaleString();
      }
      
      data = {
        bankName: bankNameMatch ? (bankNameMatch[1] || bankNameMatch[2]) : '',
        requestId: requestIdMatch ? requestIdMatch[1] : '',
        amount: formattedAmount, // Use the properly formatted amount
        rate: rateMatch ? rateMatch[1] : ''
      };
    }
    
    const translatedTitle = t(`notifications.types.${titleKey}`) || notification.title || t('notifications.noTitle');
    const translatedMessage = t(`notifications.messages.${messageKey}`, data);
    
    return {
      title: translatedTitle,
      // If translation fails or returns the key, fallback to original message
      message: (translatedMessage && translatedMessage !== `notifications.messages.${messageKey}`) 
        ? translatedMessage 
        : notification.message || t('notifications.noMessage')
    };
  };
  
  // useEffect per notifiche
  useEffect(() => {
    // Fetch notification count
    const fetchNotificationCount = async () => {
      try {
        // Add timestamp to prevent caching
        const res = await axios.get(`/api/notifications/count?t=${Date.now()}`);
        
        if (res.data && typeof res.data.count === 'number') {
          setNotificationCount(res.data.count);
        } else {
          console.error('Unexpected count response format:', res.data);
        }
      } catch (err) {
        console.error('Error fetching notification count:', err);
        setError(t('common.errors.fetchNotifications'));
      }
    };
    
    // Fetch notifications for dropdown
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        // Add timestamp to prevent caching
        const res = await axios.get(`/api/notifications?limit=4&t=${Date.now()}`);
        
        if (res.data && Array.isArray(res.data.notifications)) {
          setNotifications(res.data.notifications);
        } else {
          console.error('Unexpected notifications response format:', res.data);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching notifications:', err);
        setError(t('common.errors.fetchNotifications'));
        setLoading(false);
      }
    };
    
    // Initial fetches
    fetchNotificationCount();
    
    // Only fetch notifications when dropdown is shown
    if (showDropdown) {
      fetchNotifications();
    }
    
    // Set up interval to fetch notification count
    const interval = setInterval(fetchNotificationCount, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, [showDropdown, t]);
  
  // Handle click outside to close notification dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      const desktopRef = notificationRef.current;
      const mobileRef = mobileNotificationRef.current;
      
      // Controlla se il click è fuori da entrambi i refs
      if (desktopRef && !desktopRef.contains(event.target) &&
          mobileRef && !mobileRef.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    if (showDropdown) {
      // Attach the listener when dropdown is open
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      // Clean up the listener
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);
  
  // Funzioni handler
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };
  
  const handleNotificationClick = async (notification) => {
    try {
      // Close dropdown
      setShowDropdown(false);
      
      // Mark as read
      await axios.put(`/api/notifications/${notification._id}/read`);
      
      // Update count
      setNotificationCount(Math.max(0, notificationCount - 1));
      
      // Navigate based on notification type and user role
      if (notification.relatedEntity && notification.relatedEntity.type) {
        if (user.role === 'admin') {
          if (notification.relatedEntity.type === 'request') {
            navigate('/admin/requests');
          } else if (notification.relatedEntity.type === 'bid') {
            navigate('/admin/bids');
          } else {
            navigate('/admin/notifications');
          }
        } else if (user.role === 'bank') {
          if (notification.relatedEntity.type === 'request') {
            navigate('/bank/open-requests');
          } else if (notification.relatedEntity.type === 'bid' && notification.relatedEntity.id) {
            navigate(`/bank/bids/${notification.relatedEntity.id}`);
          } else {
            navigate('/bank/notifications');
          }
        } else { // company
          if (notification.relatedEntity.type === 'request' && notification.relatedEntity.id) {
            navigate(`/company/requests/${notification.relatedEntity.id}`);
          } else if (notification.relatedEntity.type === 'bid') {
            navigate('/company/past-requests');
          } else {
            navigate('/company/notifications');
          }
        }
      } else {
        // If no related entity, just go to notifications page
        viewAllNotifications();
      }
    } catch (err) {
      console.error('Error handling notification click:', err);
    }
  };
  
  const viewAllNotifications = () => {
    setShowDropdown(false);
    
    // Navigate to notifications page based on user role
    if (user.role === 'admin') {
      navigate('/admin/notifications');
    } else if (user.role === 'bank') {
      navigate('/bank/notifications');
    } else {
      navigate('/company/notifications');
    }
  };
  
  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return t('common.invalidDate');
      }
      
      const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
      
      if (diffInHours < 1) {
        return t('notifications.time.justNow', 'Just now');
      } else if (diffInHours < 24) {
        return t('notifications.time.hoursAgo', `{{hours}} hour${diffInHours === 1 ? '' : 's'} ago`, { hours: diffInHours });
      } else if (diffInHours < 48) {
        return t('notifications.time.yesterday', 'Yesterday');
      } else {
        return date.toLocaleDateString();
      }
    } catch (err) {
      console.error('Error formatting time:', err);
      return t('common.invalidDate');
    }
  };

  // Funzione per gestire il toggle della sidebar
  const handleToggleSidebar = () => {
    if (onToggleSidebar) {
      onToggleSidebar();
    } else {
      // Fallback: prova a trovare e cliccare il vecchio toggle
      const oldToggle = document.querySelector('.sidebar-toggle');
      if (oldToggle) {
        oldToggle.click();
      } else {
        // Fallback: toggle manuale della classe
        const sidebar = document.querySelector('.sidebar');
        const body = document.body;
        if (sidebar) {
          sidebar.classList.toggle('expanded');
          body.classList.toggle('sidebar-open');
        }
      }
    }
  };
  
  return (
    <>
      {/* Header principale */}
      <div className="header">
        {/* Pulsante menu fisso (opzioni) - Solo mobile */}
        {isMobile && (
          <button 
            className="mobile-menu-fixed"
            onClick={handleToggleSidebar}
            aria-label="Toggle menu"
          >
            <Menu size={22} />
          </button>
        )}
        
        {/* Titolo centrato */}
        <h1 style={{ color: isMobile ? 'white' : 'rgb(0,32,96)' }}>{getTranslatedTitle()}</h1>
        
        {/* Pulsante notifiche fisso - Solo mobile */}
        {isMobile && (
          <div className="mobile-notifications-fixed" ref={mobileNotificationRef}>
            <div className="notification-icon" onClick={toggleDropdown}>
              <BellRing 
                size={22} 
                className={`notification-bell ${notificationCount > 0 ? 'has-notifications' : ''}`}
              />
              {notificationCount > 0 && (
                <span className="notification-count">{notificationCount}</span>
              )}
              {showDropdown && (
                <div className="dropdown-content">
                  {loading ? (
                    <div className="notification-item">
                      <p>{t('common.loadingNotifications')}</p>
                    </div>
                  ) : error ? (
                    <div className="notification-item error">
                      <p>{error}</p>
                    </div>
                  ) : notifications.length > 0 ? (
                    <>
                      {notifications.map(notification => {
                        const translatedNotification = translateNotification(notification);
                        
                        return (
                          <div 
                            key={notification._id || `notification-${Math.random()}`} 
                            className={`notification-item ${notification.isRead ? '' : 'unread'}`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <h4>{translatedNotification.title}</h4>
                            <p>{translatedNotification.message}</p>
                            <span>{notification.createdAt ? formatTime(notification.createdAt) : t('common.invalidDate')}</span>
                          </div>
                        );
                      })}
                      <div className="notification-footer">
                        <button onClick={viewAllNotifications} className="btn btn-primary btn-sm">
                          {t('notifications.viewAll')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="notification-item">
                      <p>{t('notifications.noNotifications')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Actions per desktop */}
        {!isMobile && (
          <div className="user-actions">
            {/* Selettore di lingua solo se NON è admin */}
            {user?.role !== 'admin' && <LanguageSelector />}
            
            <div className="notification-icon" ref={notificationRef} onClick={toggleDropdown}>
              <BellRing 
                size={24} 
                className={`notification-bell ${notificationCount > 0 ? 'has-notifications' : ''}`}
              />
              {notificationCount > 0 && (
                <span className="notification-count">{notificationCount}</span>
              )}
              {showDropdown && (
                <div className="dropdown-content">
                  {loading ? (
                    <div className="notification-item">
                      <p>{t('common.loadingNotifications')}</p>
                    </div>
                  ) : error ? (
                    <div className="notification-item error">
                      <p>{error}</p>
                    </div>
                  ) : notifications.length > 0 ? (
                    <>
                      {notifications.map(notification => {
                        const translatedNotification = translateNotification(notification);
                        
                        return (
                          <div 
                            key={notification._id || `notification-${Math.random()}`} 
                            className={`notification-item ${notification.isRead ? '' : 'unread'}`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <h4>{translatedNotification.title}</h4>
                            <p>{translatedNotification.message}</p>
                            <span>{notification.createdAt ? formatTime(notification.createdAt) : t('common.invalidDate')}</span>
                          </div>
                        );
                      })}
                      <div className="notification-footer">
                        <button onClick={viewAllNotifications} className="btn btn-primary btn-sm">
                          {t('notifications.viewAll')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="notification-item">
                      <p>{t('notifications.noNotifications')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Desktop User Profile */}
            <div className="user-profile">
              <span>{user?.organization?.name || user?.username || 'Admin'}</span>
              <div className="user-menu">
                <button onClick={handleLogout} className="logout-btn-modern">
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Header;